/**
 * The welcome message — the billboard everybody reads once.
 *
 * Jay: *"when you first log on to the app, there'll be a sort of billboard that
 * appears right in front of you with text for users to read … After they close
 * it, it will not appear again unless the message has been changed. Then it'll
 * appear again for everyone … admins should be able to edit that welcome message
 * within the admin controls."*
 *
 * Three small decisions carry all of that, and they are all in this file:
 *
 *   - **Changed means a newer date, nothing cleverer.** The family's message
 *     carries when it was last saved; each member carries when they last closed
 *     it. Closed before it was saved → it is news to them, and it comes up again.
 *     No version counters to keep in step, and nothing to reset for anybody when
 *     an administrator presses Save: the new date does it for the whole family at
 *     once.
 *   - **Until an administrator writes one, the app's own words stand in** (the
 *     `greeting.default` strings). So the very first person into a new family is
 *     still met by something — and because nobody has closed *anything* yet, that
 *     is shown once too.
 *   - **Two languages, and either will do.** The family reads Hebrew and
 *     English. An administrator may write both, or only the one they are
 *     comfortable in; a reader gets their own language when it was written and
 *     the other one when it was not, rather than an empty board.
 *
 * It is plain words on purpose — paragraphs, nothing else. A welcome that needs
 * formatting is a welcome that is too long.
 */
import type { ISODateTime, Lang, WelcomeMessage } from './types';

/** Long enough for a real welcome; short enough that it is still one. */
export const MAX_WELCOME_CHARS = 1500;

/** What was typed, made storable: no stray space at the ends, no run of blank lines, no essay. */
export function tidyWelcome(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, MAX_WELCOME_CHARS);
}

/** Its paragraphs, as the board sets them: a blank line starts a new one, and a single line break is kept inside one. */
export function welcomeParagraphs(text: string): string[] {
  return tidyWelcome(text).split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
}

/**
 * What one reader is shown, and which language it turned out to be in (the board
 * sets its direction by that, not by the reader's own language).
 *
 * Their language if the administrators wrote it; the other one if they did not;
 * the app's own words if nobody has written anything at all.
 */
export function welcomeFor(welcome: WelcomeMessage | undefined, lang: Lang, builtIn: Record<Lang, string>): { text: string; lang: Lang; custom: boolean } {
  const other: Lang = lang === 'en' ? 'he' : 'en';
  const mine = tidyWelcome(welcome?.[lang] ?? '');
  if (mine) return { text: mine, lang, custom: true };
  const theirs = tidyWelcome(welcome?.[other] ?? '');
  if (theirs) return { text: theirs, lang: other, custom: true };
  return { text: builtIn[lang], lang, custom: false };
}

/**
 * Whether the board should come up by itself for this member.
 *
 * Never closed it: yes. Closed it, and the message has been saved since: yes —
 * that is "it'll appear again for everyone". Otherwise it stays out of the way,
 * and the menu is how anybody reads it again.
 */
export function welcomeIsNews(welcome: WelcomeMessage | undefined, seenAt: ISODateTime | undefined): boolean {
  if (!seenAt) return true;
  return !!welcome && seenAt < welcome.updatedAt;
}

/** The same words as are already up — so pressing Save on nothing new does not drag the whole family back to the board. */
export function sameWelcome(a: Pick<WelcomeMessage, 'en' | 'he'> | undefined, b: Pick<WelcomeMessage, 'en' | 'he'>): boolean {
  return tidyWelcome(a?.en ?? '') === tidyWelcome(b.en) && tidyWelcome(a?.he ?? '') === tidyWelcome(b.he);
}
